import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { mockStats, mockUser } from '@/mocks/userData';

interface Template {
  id: string;
  name: string;
  bg: string;
  accent: string;
  textColor: string;
}

const templates: Template[] = [
  { id: 't1', name: '蓝紫', bg: 'linear-gradient(135deg,#B5C6E0 0%,#C5B9D8 50%,#D8E4F0 100%)', accent: '#fff', textColor: '#fff' },
  { id: 't2', name: '晨雾', bg: 'linear-gradient(135deg,#F5F7FA 0%,#E8EEF8 40%,#C5B9D8 100%)', accent: '#A8B8D8', textColor: '#2D2B3D' },
  { id: 't3', name: '深蓝', bg: 'linear-gradient(135deg,#3A4F72 0%,#6B8AB8 50%,#A8B8D8 100%)', accent: '#E8EEF8', textColor: '#fff' },
  { id: 't4', name: '淡紫', bg: 'linear-gradient(135deg,#EEF2FA 0%,#DDE4F2 40%,#C5B9D8 100%)', accent: '#A8B8D8', textColor: '#3A4F72' },
];

export default function PostcardPage() {
  const navigate = useNavigate();
  const [selectedTemplate, setSelectedTemplate] = useState('t1');
  const [showLogo, setShowLogo] = useState(true);
  const [showDate, setShowDate] = useState(true);
  const [showFocusTime, setShowFocusTime] = useState(true);
  const [showHabitCount, setShowHabitCount] = useState(true);
  const [customText, setCustomText] = useState('');
  const [shareToast, setShareToast] = useState(false);
  const [saveToast, setSaveToast] = useState(false);

  const tpl = templates.find(t => t.id === selectedTemplate) ?? templates[0];
  const today = new Date();
  const dateStr = `${today.getFullYear()}.${String(today.getMonth() + 1).padStart(2, '0')}.${String(today.getDate()).padStart(2, '0')}`;
  const weekFocusHours = Math.round(mockStats.totalFocusHours * 0.15);

  const handleSave = () => {
    setSaveToast(true);
    setTimeout(() => setSaveToast(false), 2000);
  };

  const handleShare = () => {
    setShareToast(true);
    setTimeout(() => setShareToast(false), 2000);
  };

  return (
    <div className="h-full flex flex-col" style={{ background: '#F5F7FA', fontFamily: '\'Noto Sans SC\', sans-serif' }}>
      {/* 顶部导航 */}
      <div className="flex items-center justify-between px-4 pt-12 pb-3 flex-shrink-0">
        <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
          <i className="ri-arrow-left-line text-xl" style={{ color: '#A8B8D8' }} />
        </button>
        <span className="font-semibold text-sm" style={{ color: '#2D2B3D' }}>专注明信片</span>
        <button className="w-8 h-8 flex items-center justify-center cursor-pointer">
          <i className="ri-more-line text-xl" style={{ color: '#A8B8D8' }} />
        </button>
      </div>

      {/* 可滚动内容区 */}
      <div className="flex-1 overflow-y-auto px-4" style={{ scrollbarWidth: 'none' }}>
        {/* 明信片预览区 */}
        <div
          className="w-full rounded-3xl overflow-hidden mb-4 relative"
          style={{ height: 220, background: tpl.bg }}
        >
          {/* 装饰圆 */}
          <div className="absolute top-0 right-0 w-32 h-32 rounded-full" style={{ background: 'rgba(255,255,255,0.08)', transform: 'translate(30%,-30%)' }} />
          <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full" style={{ background: 'rgba(255,255,255,0.06)', transform: 'translate(-30%,30%)' }} />

          <div className="absolute inset-0 flex flex-col justify-between p-6">
            {/* 顶部：Logo + 日期 */}
            <div className="flex items-center justify-between">
              {showLogo && (
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.25)' }}>
                    <i className="ri-leaf-line text-sm" style={{ color: tpl.accent }} />
                  </div>
                  <span className="font-semibold text-sm" style={{ color: tpl.textColor, opacity: 0.9 }}>轻醒</span>
                </div>
              )}
              {showDate && (
                <span className="text-xs" style={{ color: tpl.textColor, opacity: 0.75 }}>{dateStr}</span>
              )}
            </div>

            {/* 中部：核心数据 */}
            <div className="flex flex-col items-center">
              {showFocusTime && (
                <div className="text-center">
                  <div className="font-bold tabular-nums" style={{ fontSize: 52, color: tpl.textColor, lineHeight: 1, letterSpacing: -1 }}>
                    {mockStats.todayFocusMinutes}
                  </div>
                  <div className="text-sm mt-1" style={{ color: tpl.textColor, opacity: 0.8 }}>分钟专注</div>
                </div>
              )}
              {showHabitCount && (
                <div className="mt-2 px-3 py-1 rounded-full" style={{ background: 'rgba(255,255,255,0.2)' }}>
                  <span className="text-xs" style={{ color: tpl.textColor }}>完成 {mockStats.habitProgress.done}/{mockStats.habitProgress.total} 个习惯</span>
                </div>
              )}
            </div>

            {/* 底部：自定义文案 + 用户名 */}
            <div className="flex items-end justify-between">
              <span className="text-xs italic" style={{ color: tpl.textColor, opacity: 0.8, maxWidth: '70%' }}>
                {customText || '每一分钟都是成长'}
              </span>
              <span className="text-xs" style={{ color: tpl.textColor, opacity: 0.6 }}>{mockUser.nickname}</span>
            </div>
          </div>
        </div>

        {/* 模板选择条 */}
        <div className="mb-4">
          <p className="text-xs font-medium mb-2" style={{ color: '#8E8E9A' }}>选择模板</p>
          <div className="flex gap-3 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
            {templates.map(t => (
              <button
                key={t.id}
                onClick={() => setSelectedTemplate(t.id)}
                className="flex-shrink-0 cursor-pointer active:scale-95 transition-transform"
              >
                <div
                  className="w-16 h-10 rounded-xl"
                  style={{
                    background: t.bg,
                    border: selectedTemplate === t.id ? '2.5px solid #A8B8D8' : '2.5px solid transparent',
                    boxShadow: selectedTemplate === t.id ? '0 0 0 1px rgba(155,142,196,0.3)' : 'none',
                  }}
                />
                <p className="text-center mt-1" style={{ fontSize: 10, color: selectedTemplate === t.id ? '#A8B8D8' : '#B0B0B0' }}>{t.name}</p>
              </button>
            ))}
          </div>
        </div>

        {/* 数据展示选项 */}
        <div
          className="rounded-2xl p-4 mb-4"
          style={{ background: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.7)' }}
        >
          <p className="text-xs font-medium mb-3" style={{ color: '#8E8E9A' }}>展示内容</p>
          {[
            { label: '显示羽芽Logo水印', value: showLogo, set: setShowLogo },
            { label: '显示日期', value: showDate, set: setShowDate },
            { label: '显示专注时长', value: showFocusTime, set: setShowFocusTime },
            { label: '显示打卡习惯数', value: showHabitCount, set: setShowHabitCount },
          ].map((opt, i) => (
            <div key={i} className="flex items-center justify-between py-2">
              <span className="text-sm" style={{ color: '#2D2B3D' }}>{opt.label}</span>
              <button
                onClick={() => opt.set(!opt.value)}
                className="w-5 h-5 rounded flex items-center justify-center cursor-pointer transition-all"
                style={{
                  background: opt.value ? 'linear-gradient(135deg,#B5C6E0,#C5B9D8)' : 'transparent',
                  border: opt.value ? 'none' : '1.5px solid #C5B9D8',
                }}
              >
                {opt.value && <i className="ri-check-line text-white" style={{ fontSize: 11 }} />}
              </button>
            </div>
          ))}
        </div>

        {/* 自定义文案 */}
        <div
          className="rounded-2xl px-4 py-3 mb-4 flex items-center gap-3"
          style={{ background: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.7)' }}
        >
          <i className="ri-edit-2-line text-base flex-shrink-0" style={{ color: '#C5B9D8' }} />
          <input
            className="flex-1 bg-transparent outline-none text-sm"
            style={{ color: '#2D2B3D', fontFamily: '\'Noto Sans SC\', sans-serif' }}
            placeholder="添加一句鼓励的话..."
            value={customText}
            onChange={e => setCustomText(e.target.value)}
            maxLength={30}
          />
        </div>

        {/* 本周数据摘要 */}
        <div
          className="rounded-2xl p-4 mb-4"
          style={{ background: 'rgba(232,226,245,0.6)', border: '1px solid rgba(155,142,196,0.2)' }}
        >
          <p className="text-xs font-medium mb-2" style={{ color: '#A8B8D8' }}>本周数据</p>
          <div className="flex gap-4">
            <div className="text-center">
              <div className="font-bold" style={{ fontSize: 20, color: '#A8B8D8' }}>{weekFocusHours}</div>
              <div className="text-xs" style={{ color: '#8E8E93' }}>小时专注</div>
            </div>
            <div className="text-center">
              <div className="font-bold" style={{ fontSize: 20, color: '#A8B8D8' }}>{mockStats.streakDays}</div>
              <div className="text-xs" style={{ color: '#8E8E93' }}>天连续</div>
            </div>
            <div className="text-center">
              <div className="font-bold" style={{ fontSize: 20, color: '#A8B8D8' }}>{mockStats.totalCheckIns}</div>
              <div className="text-xs" style={{ color: '#8E8E93' }}>次打卡</div>
            </div>
          </div>
        </div>

        <div className="pb-4" />
      </div>

      {/* 底部双按钮 */}
      <div className="flex gap-3 px-4 pb-8 pt-3 flex-shrink-0" style={{ borderTop: '1px solid rgba(155,142,196,0.12)' }}>
        <button
          onClick={handleSave}
          className="flex-1 h-12 rounded-2xl font-semibold text-white cursor-pointer whitespace-nowrap active:scale-95 transition-transform"
          style={{ background: 'linear-gradient(135deg,#B5C6E0,#A8B8D8)', fontSize: 14, boxShadow: '0 6px 20px rgba(181,198,224,0.45)' }}
        >
          保存到相册
        </button>
        <button
          onClick={handleShare}
          className="flex-1 h-12 rounded-2xl font-semibold cursor-pointer whitespace-nowrap active:scale-95 transition-transform"
          style={{ color: '#A8B8D8', fontSize: 14, border: '1.5px solid #A8B8D8', background: 'transparent' }}
        >
          分享
        </button>
      </div>

      {/* Toast 提示 */}
      {(saveToast || shareToast) && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 px-6 py-3 rounded-2xl" style={{ background: 'rgba(45,43,61,0.85)', backdropFilter: 'blur(12px)' }}>
          <p className="text-white text-sm text-center">{saveToast ? '已保存到相册' : '分享面板已打开'}</p>
        </div>
      )}
    </div>
  );
}
